/**
 * The Sources subtab: source list + upload, auto-ingest → origin-routed review.
 *
 * Lists `type='source'` documents, uploads new transcripts/files, and views a
 * selected source with provenance. **Uploading a source auto-triggers the
 * governed ingestion pipeline** (`POST /api/ingestion/runs`). A transcript
 * ingestion can propose ~30 changes, so its review is **NOT** crammed into the
 * chat-inline box (#6): a transcript-origin run opens a **full-size review
 * panel** via {@link ReviewControlContext} — the panel is owned by
 * {@link AppContent} so it survives tab switches (e.g. "View card details"
 * inside the review). A chat-origin run (rare from this path) still routes to
 * the app-shell chat via {@link useChatControl} (`openPendingReview`). The
 * per-source **"Re-ingest"** button re-runs ingestion for an edited source or
 * after a rejected run. Ingestion targets the entity graph directly (no
 * Blueprint document required).
 *
 * The sources list can be collapsed to a narrow rail ({@link SourceRail}, `w-12`),
 * displaying two-letter abbreviation buttons per source (toggled via `toggle-sources-list`),
 * mirroring `DocumentsPanel`'s `DocumentRail` to maximize reading width for the
 * {@link SourceViewer}. The expanded toolbar groups search alongside compact
 * icon buttons for sort field, sort direction, refresh sources, and add source toggle.
 *
 * The Care Delivery AI chat is no longer embedded here — it docks once at the
 * app-shell level ({@link AppContent}). Source content is PHI; nothing is logged.
 *
 * **Auto-populates warehouse sources on view** (ADR-047 addendum, BLA-237):
 * {@link usePopulateSources} AWAITS `POST /api/documents/populate-sources`
 * once per patient view, showing a visible loading spinner for its duration
 * (measured ~6s for a one-source patient in dev) and refetching the document
 * list once it materialises new sources. A "Refresh" button re-runs it
 * explicitly, bypassing the staleness window. This replaced an earlier
 * fire-and-forget backend design that scheduled the populate via FastAPI
 * `BackgroundTasks` from `GET /api/documents` — that failed SILENTLY on a
 * Cloud Run revision with `cpu-throttling=true` (CPU throttled to near-zero
 * outside request handling), and this panel had no refetch/poll to notice
 * even a successful completion. An honest spinner beats a list that may
 * never fill.
 */
import React, { useEffect, useState } from "react";
import { useDocumentLibrary } from "../../hooks/useDocumentLibrary";
import { usePopulateSources } from "../../hooks/usePopulateSources";
import { useSources } from "../../hooks/useSources";
import { useChatControl } from "../ChatControlContext";
import { useReviewControl } from "../ReviewControlContext";
import { IngestProgress } from "./IngestProgress";
import { AddSourceModal } from "./AddSourceModal";
import SourceList from "./SourceList";
import SourceViewer from "./SourceViewer";
import type { SourceReveal } from "../blueprint/citationResolve";
import type { BlueprintDocument, IngestionRun } from "../../types";

import { parseEncounterDateFromTitle } from "./sourceDisplay";
import { isAssessmentKind } from "../../utils/sourceKindLabels";

/** Props for {@link SourcesPanel}. */
interface SourcesPanelProps {
  /** The active patient id. */
  patientId: string;
  /**
   * A pending citation reveal (from "Go to source"): open this source and
   * scroll to + flash the cited span. Null when no reveal is pending.
   */
  reveal?: SourceReveal | null;
  /** Clear the pending reveal — called when the provider manually picks a source. */
  onClearReveal?: () => void;
}

/** Check whether a source document is a clinical encounter note rather than an assessment. */
// eslint-disable-next-line react-refresh/only-export-components
export function isClinicalSessionNote(doc: BlueprintDocument): boolean {
  if (doc.type !== "source") return false;
  return !isAssessmentKind(doc.source_kind);
}

/**
 * Find the latest non-assessment clinical session note, falling back to the
 * latest source document if none match.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function findLatestClinicalNote(
  docs: BlueprintDocument[],
): BlueprintDocument | null {
  const sources = docs.filter((d) => d.type === "source");
  if (sources.length === 0) return null;

  const clinical = sources.filter(isClinicalSessionNote);
  const candidates = clinical.length > 0 ? clinical : sources;

  const sorted = [...candidates].sort((a, b) => {
    const encA = parseEncounterDateFromTitle(a.title);
    const encB = parseEncounterDateFromTitle(b.title);
    if (encA && encB) {
      return encB.getTime() - encA.getTime();
    }
    if (encA && !encB) {
      return -1;
    }
    if (!encA && encB) {
      return 1;
    }
    const createdA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const createdB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return createdB - createdA;
  });

  return sorted[0] ?? null;
}

/**
 * A two-letter abbreviation for a source's rail button.
 */
function railAbbreviation(title: string): string {
  return title.trim().slice(0, 2).toUpperCase();
}

/** Props for {@link SourceRail}. */
interface SourceRailProps {
  /** All of the patient's documents. */
  documents: BlueprintDocument[];
  /** The currently selected document id, if any. */
  selectedId: string | null;
  /** Select a document. */
  onSelect: (documentId: string) => void;
}

/** The collapsed narrow-rail view of the sources list. */
function SourceRail({
  documents,
  selectedId,
  onSelect,
}: SourceRailProps): React.ReactElement {
  const sources = documents.filter((d) => d.type === "source");
  return (
    <ul className="space-y-1" data-testid="sources-rail">
      {sources.map((doc) => (
        <li key={doc.id}>
          <button
            type="button"
            title={doc.title}
            aria-label={doc.title}
            aria-pressed={doc.id === selectedId}
            onClick={() => onSelect(doc.id)}
            className={`flex h-8 w-8 items-center justify-center rounded-lg text-[11px] font-semibold ${
              doc.id === selectedId
                ? "bg-blue-50 border border-blue-300 text-blue-700"
                : "border border-transparent text-slate-500 hover:bg-slate-50"
            }`}
          >
            {railAbbreviation(doc.title)}
          </button>
        </li>
      ))}
    </ul>
  );
}

/** The run produced an inline chat review vs the full-size transcript panel. */
function isChatOrigin(run: IngestionRun): boolean {
  return run.origin === "chat";
}

/**
 * Render the Sources subtab for a patient.
 *
 * @param props - The active patient id.
 * @returns The sources-panel element.
 */
export const SourcesPanel: React.FC<SourcesPanelProps> = ({
  patientId,
  reveal = null,
  onClearReveal,
}) => {
  const library = useDocumentLibrary(patientId, "source");
  const sources = useSources(patientId);
  const populate = usePopulateSources(patientId);
  const chatControl = useChatControl();
  const reviewControl = useReviewControl();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [internalReveal, setInternalReveal] = useState<SourceReveal | null>(
    null,
  );
  const [listOpen, setListOpen] = useState(true);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [prevPatientId, setPrevPatientId] = useState(patientId);
  if (patientId !== prevPatientId) {
    setPrevPatientId(patientId);
    setSelectedId(null);
    setInternalReveal(null);
    setHasAutoSelected(false);
  }
  const { loadSource } = sources;
  const { reload: reloadLibrary } = library;

  // Once the awaited populate-sources call actually materialised sources,
  // refetch the document list so they appear (ADR-047 addendum — the awaited
  // redesign; the panel's own `populate.populating` spinner below is the
  // visible loading state while this is in flight).
  const populateResult = populate.result;
  useEffect(() => {
    if (populateResult?.populated) {
      reloadLibrary();
    }
  }, [populateResult, reloadLibrary]);

  // A citation reveal targets a specific source: select it so SourceViewer can
  // scroll to + flash the cited span. Selecting is done with the React "adjust
  // state when a prop changes" pattern (a guarded setState during render, NOT an
  // effect); tracking the last-seen key means a repeat reveal re-selects while a
  // manual pick in between still sticks.
  const [seenRevealKey, setSeenRevealKey] = useState<number | undefined>(
    undefined,
  );
  if (reveal && reveal.key !== seenRevealKey) {
    setSeenRevealKey(reveal.key);
    setSelectedId(reveal.target.sourceId);
    setInternalReveal(null);
  }

  // Initial load: when no selection, not ingesting, and no active reveal, automatically select
  // the latest non-assessment clinical session note during render.
  const patientDocs = library.documents.filter(
    (d) => d.patient_id === patientId,
  );
  if (
    !hasAutoSelected &&
    selectedId === null &&
    !reveal &&
    !sources.ingesting &&
    patientDocs.length > 0
  ) {
    const target = findLatestClinicalNote(patientDocs);
    if (target) {
      setHasAutoSelected(true);
      setSelectedId(target.id);
    }
  }

  // Fetch the active source's body when selectedId changes.
  useEffect(() => {
    if (selectedId) {
      void loadSource(selectedId);
    }
  }, [selectedId, loadSource]);

  // Fetch the revealed source's body (an external read — belongs in an effect,
  // not the render above). `loadSource` is a stable callback.
  const revealKey = reveal?.key;
  const revealSourceId = reveal?.target.sourceId;
  useEffect(() => {
    if (revealKey !== undefined && revealSourceId !== undefined) {
      void loadSource(revealSourceId);
    }
  }, [revealKey, revealSourceId, loadSource]);

  // The reveal applies only while its target source is the one on screen.
  // In-panel search body-mention selections (internalReveal) take precedence
  // over external citation reveals on the same document.
  const activeReveal =
    internalReveal && selectedId === internalReveal.target.sourceId
      ? internalReveal
      : reveal && selectedId === reveal.target.sourceId
        ? reveal
        : null;

  const selected = patientDocs.find((d) => d.id === selectedId) ?? null;

  const handleSelect = (id: string): void => {
    setSelectedId(id);
  };

  const handleManualSelect = (id: string, snippet?: string): void => {
    onClearReveal?.();
    handleSelect(id);
    if (snippet) {
      setInternalReveal({
        target: {
          sourceId: id,
          lines: [],
          snippet: snippet.trim(),
        },
        key: Date.now(),
      });
    } else {
      setInternalReveal(null);
    }
  };

  // Route a fresh ingestion run by origin: chat-origin → surface inline in the
  // app-shell chat; transcript-origin (the common case; also the default when
  // the additive `origin` field is absent) → open the full-size review panel
  // via ReviewControlContext (owned by AppContent so it survives tab switches).
  const routeReview = (run: IngestionRun): void => {
    if (isChatOrigin(run)) {
      chatControl.openPendingReview();
    } else {
      reviewControl.openReview(run);
    }
  };

  const handleUpload = (title: string, body: string): Promise<void> => {
    // Upload auto-ingests (useSources.upload); route the resulting proposal to
    // the full-size panel (transcript) or the chat (chat-origin).
    return sources.upload(title, body).then((result) => {
      if (result) {
        library.reload();
        handleSelect(result.doc.id);
        if (result.run) routeReview(result.run);
      }
    });
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<"date" | "alphabetical">("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [isAddOpen, setIsAddOpen] = useState(false);

  const toggleSortField = (): void => {
    setSortField((prev) => (prev === "date" ? "alphabetical" : "date"));
  };

  const toggleSortDirection = (): void => {
    setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  return (
    <div className="flex h-full overflow-hidden">
      <div
        className={`shrink-0 border-r border-slate-200 overflow-y-auto ${
          listOpen ? "w-80 p-4" : "w-12 p-2"
        }`}
      >
        <div
          className={`flex items-center mb-2 ${
            listOpen ? "justify-between" : "justify-center"
          }`}
        >
          {listOpen && (
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Sources
            </span>
          )}
          <button
            type="button"
            data-testid="toggle-sources-list"
            aria-label={
              listOpen ? "Collapse sources list" : "Expand sources list"
            }
            onClick={() => setListOpen((open) => !open)}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            {listOpen ? "«" : "»"}
          </button>
        </div>
        {listOpen ? (
          <>
            <div className="flex items-center gap-1.5 mb-2">
              <div className="relative flex-1 min-w-0">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search sources…"
                  aria-label="Search sources"
                  data-testid="sources-search-input"
                  className="w-full pl-7 pr-6 py-1 text-xs rounded-md border border-slate-300 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                />
                <svg
                  className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-2 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 1114 0z"
                  />
                </svg>
                {searchQuery !== "" && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                    data-testid="sources-search-clear"
                    className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600 text-xs font-bold leading-none"
                  >
                    ×
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={toggleSortField}
                data-testid="sources-sort-field-button"
                title={
                  sortField === "date" ? "Sort alphabetically" : "Sort by date"
                }
                aria-label={
                  sortField === "date" ? "Sort alphabetically" : "Sort by date"
                }
                className="w-7 h-7 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors text-xs shrink-0"
              >
                {sortField === "date" ? (
                  <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                ) : (
                  <span className="font-bold text-[11px] leading-none">
                    A-Z
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={toggleSortDirection}
                data-testid="sources-sort-direction-button"
                title={
                  sortDirection === "desc"
                    ? "Sort ascending"
                    : "Sort descending"
                }
                aria-label={
                  sortDirection === "desc"
                    ? "Sort ascending"
                    : "Sort descending"
                }
                className="w-7 h-7 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors text-xs shrink-0"
              >
                {sortDirection === "desc" ? (
                  <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <polyline points="19 12 12 19 5 12" />
                  </svg>
                ) : (
                  <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="19" x2="12" y2="5" />
                    <polyline points="5 12 12 5 19 12" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                onClick={() => populate.refresh()}
                disabled={populate.populating}
                aria-label="Refresh sources"
                title="Refresh sources"
                data-testid="refresh-sources-button"
                className="w-7 h-7 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors text-xs shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg
                  className={`w-3.5 h-3.5 ${populate.populating ? "animate-spin text-blue-600" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => setIsAddOpen((prev) => !prev)}
                aria-label="Add source"
                title="Add source"
                data-testid="add-source-header-button"
                className={`w-7 h-7 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors text-xs shrink-0 ${
                  isAddOpen ? "bg-slate-100 text-blue-600 border-blue-300" : ""
                }`}
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </button>
            </div>
            {populate.populating && (
              <IngestProgress
                testId="populate-sources-progress"
                className="mb-3"
                message="Loading sources from the record system…"
              />
            )}
            {!populate.populating && populate.result?.status === "failed" && (
              <div
                role="alert"
                data-testid="populate-sources-failed-banner"
                className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 mb-3 text-xs text-rose-800"
              >
                Couldn&apos;t load sources from the record system just now. Try
                Refresh, or check back later.
              </div>
            )}
            {!populate.populating &&
              populate.result?.status === "not_applicable" && (
                <div
                  data-testid="populate-sources-not-applicable-notice"
                  className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 mb-3 text-xs text-slate-500"
                >
                  No warehouse data for this patient.
                </div>
              )}
            <SourceList
              documents={library.documents}
              selectedId={selectedId}
              onSelect={handleManualSelect}
              searchQuery={searchQuery}
              patientId={patientId}
              sortField={sortField}
              sortDirection={sortDirection}
            />
          </>
        ) : (
          <SourceRail
            documents={library.documents}
            selectedId={selectedId}
            onSelect={handleManualSelect}
          />
        )}
      </div>

      <AddSourceModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onUpload={handleUpload}
        ingesting={sources.ingesting}
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {sources.ingestionHalted && (
          <div
            role="alert"
            data-testid="ingestion-halted-banner"
            className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          >
            Ingestion is currently halted. This is a deliberate, temporary
            operational state, not an error — the source was saved, but it has
            not been reviewed for proposed changes. Try again once ingestion
            resumes.
          </div>
        )}
        {selected === null &&
          (sources.ingesting ? (
            <IngestProgress testId="ingesting-placeholder" />
          ) : (
            <p className="text-sm text-slate-400 italic">
              Select or upload a source to view it.
            </p>
          ))}
        {selected !== null && (
          <SourceViewer
            document={selected}
            version={sources.version}
            ingesting={sources.ingesting}
            reveal={activeReveal}
          />
        )}
      </div>
    </div>
  );
};

export default SourcesPanel;
